// @flow
import { useState, useRef, useEffect, useMemo } from 'react';
// Styles
import './UtmSourceAttributionModal.css';
// Constantsx
import { DEFAULT_CHANNELS } from '../../constants/utmSourceChannels';

type UtmSourceAttributionModalProps = {
  isOpen: boolean,
  onClose: () => void,
  onSubmit: (utmSource: string) => void,
};

const UtmSourceAttributionModal = ({
  isOpen,
  onClose,
  onSubmit,
}: UtmSourceAttributionModalProps) => {
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const cardRef = useRef(null);
  const searchInputRef = useRef(null);

  const mainSourceChannels = DEFAULT_CHANNELS.filter(channel => channel.isMain);

  const filteredSecondaryChannels = useMemo(() => {
    const secondaryChannels = DEFAULT_CHANNELS.filter(
      channel => !channel.isMain,
    );
    const term = searchTerm.trim().toLowerCase();
    if (!term) return secondaryChannels;
    return secondaryChannels.filter(channel =>
      channel.label.toLowerCase().includes(term),
    );
  }, [searchTerm]);

  const handleSelect = (id: string) => {
    setSelectedChannelId(id);
  };

  const handleConfirm = () => {
    const channel = DEFAULT_CHANNELS.find(c => c.id === selectedChannelId);
    if (channel) onSubmit(channel.utmSource);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cardRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="utm-source-attribution-modal-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={cardRef}
        className="utm-source-attribution-modal-card"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="utm-source-attribution-modal-header">
          <h2 className="utm-source-attribution-modal-title">
            Como você descobriu o Big Delivery?
          </h2>
          <button
            type="button"
            className="utm-source-attribution-modal-close-btn"
            onClick={onClose}
            aria-label="Fechar"
          >
            <i class="fa fa-times-circle"></i>
          </button>
        </div>

        <p className="utm-source-attribution-modal-description">
          Essa pesquisa nos ajudará a fornecer um serviço ainda melhor para
          você!
        </p>

        <div className="utm-source-attribution-modal-top-grid">
          {mainSourceChannels.map(channel => {
            const isSelected = selectedChannelId === channel.id;
            return (
              <button
                key={channel.id}
                type="button"
                className={`utm-source-attribution-modal-chip-button ${isSelected ? 'selected' : ''}`}
                aria-pressed={isSelected}
                onClick={() => handleSelect(channel.id)}
              >
                <span className="utm-source-attribution-modal-chip-label">
                  {channel.label}
                </span>
                <span
                  className="utm-source-attribution-modal-check-mark"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <div className="utm-source-attribution-modal-secondary-area">
          <button
            type="button"
            className="utm-source-attribution-modal-toggle-link"
            onClick={() => setIsSearchExpanded(previous => !previous)}
            aria-expanded={isSearchExpanded}
          >
            <span>Outro canal de divulgação</span>
            <span
              className={`utm-source-attribution-modal-toggle-chevron ${isSearchExpanded ? 'open' : ''}`}
            />
          </button>

          <div
            className={`utm-source-attribution-modal-utm-collapse ${isSearchExpanded ? 'open' : ''}`}
            aria-hidden={!isSearchExpanded}
          >
            <div className="utm-source-attribution-modal-utm-collapse-inner">
              <div className="utm-source-attribution-modal-search-container">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="utm-source-attribution-modal-search-input"
                  placeholder="Buscar canal"
                  aria-label="Buscar canal"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />

                <div className="utm-source-attribution-modal-dropdown-list">
                  {filteredSecondaryChannels.length > 0 ? (
                    filteredSecondaryChannels.map(channel => {
                      const isSelected = selectedChannelId === channel.id;
                      return (
                        <button
                          key={channel.id}
                          type="button"
                          className={`utm-source-attribution-modal-dropdown-item ${isSelected ? 'selected' : ''} "`}
                          aria-pressed={isSelected}
                          onClick={() => handleSelect(channel.id)}
                        >
                          <span>{channel.label}</span>
                          <span
                            className="utm-source-attribution-modal-check-mark"
                            aria-hidden="true"
                          />
                        </button>
                      );
                    })
                  ) : (
                    <div className="utm-source-attribution-modal-empty-result">
                      Nenhum canal encontrado
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="utm-source-attribution-modal-footer">
          <button
            type="button"
            className="utm-source-attribution-modal-btn-skip"
            onClick={onClose}
          >
            Agora não
          </button>
          <button
            type="button"
            className="utm-source-attribution-modal-btn-submit"
            disabled={!selectedChannelId}
            onClick={handleConfirm}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default UtmSourceAttributionModal;
